import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  catchError,
  delay,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { CiaoSharedModule } from '~app/components/shared/shared.module';

import { CiaoModalComponent } from '~app/components/shared/ciao-modal/ciao-modal.component';
import { AuthService, PersonService } from '~app/services';
import { BehaviorSubject, Subscription, combineLatest, of } from 'rxjs';
import { UserModalFormComponent } from './type';
import { RolePermissionService } from '~app/services/role-permission.service';
import { ToastrService } from 'ngx-toastr';
import { UserDetailFormComponent } from './user-details-form/user-details-form.component';

@Component({
    imports: [
        CiaoSharedModule,
        AsyncPipe,
        UserDetailFormComponent,
    ],
    selector: 'ciao-user-modal',
    templateUrl: './user-modal.component.html',
    styleUrls: ['./user-modal.component.less']
})
export class UserModalComponent implements OnInit {
  readonly personId$ = new BehaviorSubject<string>(null);
  readonly person$ = this.personId$.pipe(
    filter((id) => !!id),
    switchMap((id) => this.personService.findById(id)),
    map((person) => {
      return person;
    }),
    shareReplay(1)
  );
  readonly personDisplayName$ = this.person$.pipe(
    map((person) => person.displayName),
    shareReplay(1)
  );
  readonly currentPersonId$ = this.authService.currentUser$.pipe(
    map((user) => user?.person?.id),
    shareReplay(1)
  );
  readonly modalTitle$ = combineLatest([
    this.personId$,
    this.currentPersonId$,
  ]).pipe(
    switchMap(([personId, currentPersonId]) => {
      if (!personId) {
        return of('New User Profile');
      } else if (personId === currentPersonId) {
        return of('My Profile');
      } else {
        return this.personDisplayName$;
      }
    })
  );

  get isAdmin() {
    return this.rolePermissionService.isAdmin$;
  }

  /**
   * Delete is only offered when the current user is a System Admin, the modal
   * is editing an existing person, and that person is not the current user
   * (no self-deletion). The backend enforces the same System-Admin rule.
   */
  readonly canDelete$ = combineLatest([
    this.personId$,
    this.currentPersonId$,
    this.rolePermissionService.isSystemAdmin$,
  ]).pipe(
    map(
      ([personId, currentPersonId, isSystemAdmin]) =>
        isSystemAdmin && !!personId && personId !== currentPersonId
    ),
    shareReplay(1)
  );

  get id() {
    return this.personId$.value;
  }
  @Input() set id(value: string) {
    this.personId$.next(value);
    this.cdr.detectChanges();
    this.currentForm?.refreshPerson();
  }
  @ViewChild('userModal') userModal: CiaoModalComponent;
  @ViewChild('currentForm') currentForm: UserModalFormComponent;
  @ViewChild('deleteModal') deleteModal: CiaoModalComponent;

  get saveButtonMessage() {
    return this.id ? 'Update and Save' : 'Save User';
  }

  get formIsValid() {
    return this.currentForm?.formGroup?.valid;
  }

  isSaving = false;
  isDeleting = false;

  subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private personService: PersonService,
    private rolePermissionService: RolePermissionService,
    private toastrService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  openModal() {
    this.isSaving = false;
    this.currentForm?.refreshPerson();
    this.userModal.openModal();
  }

  closeEditModal() {
    this.userModal.close('back to view');
  }

  saveData() {
    if (this.isSaving) {
      console.warn('Attempting to save multiple times');
      return;
    }
    this.isSaving = true;
    this.currentForm
      .saveFormData()
      .pipe(
        tap(() => {
          this.userModal.close('user modal attempted to save');
        }),
        catchError((err, caught) => {
          console.error(err);
          return of(null).pipe(delay(1000));
        }),
        tap(() => (this.isSaving = false))
      )
      .subscribe();
  }

  /** Opens the "are you sure" confirmation for a permanent delete. */
  confirmDelete() {
    this.deleteModal.openModal();
  }

  /**
   * Hard-deletes the person after confirmation (System Admin only). Closes both
   * the confirmation and the user modal on success and surfaces a toast either
   * way. The backend re-checks the System-Admin rule.
   */
  deleteData() {
    if (this.isDeleting) {
      return;
    }
    const personId = this.id;
    if (!personId) {
      return;
    }
    this.isDeleting = true;
    this.personService.deletePerson(personId, true).subscribe({
      next: () => {
        this.isDeleting = false;
        this.toastrService.success('User deleted.');
        this.deleteModal.close('user deleted');
        this.userModal.close('user deleted');
      },
      error: (err) => {
        console.error(err);
        // Re-enable the buttons but leave the confirmation modal open so the
        // user can retry or cancel themselves. (Dismissing it here while
        // `isDeleting` is flipping triggers an NG0100 change-detection error.)
        this.isDeleting = false;
        this.toastrService.error(this.deleteErrorMessage(err));
      },
    });
  }

  /**
   * Prefer the server's specific reason (e.g. 409 "checked out on an active
   * trip", 403 "cannot delete your own account") over a generic message, so
   * the admin understands why the delete was blocked.
   */
  private deleteErrorMessage(err: unknown): string {
    const serverErrors = (err as { error?: { errors?: unknown } })?.error
      ?.errors;
    const status = (err as { status?: number })?.status;
    if ((status === 409 || status === 403) && serverErrors) {
      const message = Array.isArray(serverErrors)
        ? serverErrors.join(' ')
        : String(serverErrors);
      if (message.trim()) {
        return message;
      }
    }
    return 'Failed to delete user. Please try again or contact support.';
  }
}
